/**
 * Step 6 — Risk Assessment.
 *
 * Spinner / failed-hero / table dispatcher; Add / Edit / Delete via
 * shared FormModal + useConfirm dialog; treatment_decision filter via
 * FilterDrawer; hierarchical Current/Child split when
 * `analysis_mode === 'hierarchical'`; CSV import/export.
 */
import React, { useState } from 'react';
import { LoadingState, FailedState } from '../../../components/ui/LoadingState';

import { useConfirm } from '../../../components/ui/AlertDialog';
import { Icon } from '../../../components/ui/Icon';
import RiskTable from './step6-risk/RiskTable';
import RiskFormModal from './step6-risk/RiskFormModal';
import HierarchicalRiskReview from './step6-risk/HierarchicalRiskReview';
import RiskMatrix, { type MatrixFilter } from './step6-risk/RiskMatrix';
import type { ChildRiskView } from './step6-risk/ChildRisksTable';
import { type RiskRecord } from './step6-risk/riskFieldSchema';
import { exportRisksToCsv, parseRiskFile } from './step6-risk/riskCsv';
import type { Step6BodyProps } from './stepBodyProps';
import { notify } from '../../../services/notificationBus';
import styles from './Step6RiskAssessment.module.css';

// LoadingState/FailedState are JSX primitives; cast for use from TSX
// (mirrors the ButtonAny convention across the step-components).
const LoadingStateAny = LoadingState as unknown as React.ComponentType<any>;
const FailedStateAny = FailedState as unknown as React.ComponentType<any>;


type ModalState =
  | null
  | { kind: 'add' }
  | { kind: 'edit'; risk: RiskRecord };

export default function Step6RiskAssessment({
  status,
  editable,
  structured,
  crud,
  assetOptions,
  threatOptions,
  scenarioOptions,
  attackPathOptions,
  apIdToAssetId,
  impactByDamageId,
  feasibilityByThreatId,
  hierarchical,
  errorMessage,
}: Step6BodyProps) {
  const [modal, setModal] = useState<ModalState>(null);
  const { confirm, confirmDialog } = useConfirm();
  // Heat-map cell click narrows the table to one priority × value
  // bucket. `null` = no matrix filter, table shows the full set.
  // Coexists with the table's internal treatment_decision filter
  // drawer (both filters apply via AND).
  const [matrixFilter, setMatrixFilter] = useState<MatrixFilter | null>(null);

  if (status === 'failed') {
    return (
      <FailedStateAny
        variant="page"
        title="Risk assessment failed"
        description={errorMessage ?? structured.error ?? 'The risk assessment agent encountered an error. Revise and retry.'}
      />
    );
  }

  // Row decoration before the table sees the data:
  // 1. Backfill `asset_id` for rows where the agent didn't emit it
  //    directly. Newer / hierarchical risk records carry `attack_path_id`
  //    instead — chase it through `apIdToAssetId` (built in the hook from
  //    the Step 5 attack-paths fetch). Row's own `asset_id` always wins
  //    when present.
  // 2. Derive `impact_rating` / `attack_feasibility_rating` from their
  //    source records (Step 3 / Step 5 joins built in the hook). The
  //    DERIVED value wins over the row's own copy — the agent often
  //    leaves these blank or 'medium', and cascade recalculations update
  //    the source records, not the risk rows (AUMOVIO TC-052).
  const rawRisks = (structured.data ?? []) as RiskRecord[];
  const risks: RiskRecord[] = rawRisks.map((r) => {
    const out: RiskRecord = { ...r };

    if (!out.asset_id) {
      const apId = (r as Record<string, unknown>).attack_path_id;
      if (typeof apId === 'string' && apIdToAssetId[apId]) {
        out.asset_id = apIdToAssetId[apId];
      }
    }

    // Older rows store the damage link as `damage_scenario_id`.
    const damageId = String(r.damage_scenario_id ?? r.damage_id ?? '');
    const derivedImpact = damageId ? impactByDamageId[damageId] : undefined;
    if (derivedImpact || r.impact_rating) {
      out.impact_rating = derivedImpact ?? r.impact_rating;
    }

    const threatId = typeof r.threat_id === 'string' ? r.threat_id : '';
    const derivedFeasibility = threatId ? feasibilityByThreatId[threatId] : undefined;
    if (derivedFeasibility || r.attack_feasibility_rating) {
      out.attack_feasibility_rating = derivedFeasibility ?? r.attack_feasibility_rating;
    }

    return out;
  });

  const noDataYet = risks.length === 0 && !structured.data;
  if (
    status === 'empty' ||
    status === 'generating' ||
    status === 'revising' ||
    (structured.loading && noDataYet)
  ) {
    return (
      <LoadingStateAny
        variant="page"
        title="Assessing risks"
        description="The risk assessment agent is computing CAL-style risk values + treatment decisions. This usually takes 1–2 minutes."
      />
    );
  }

  const handleSave = async (values: Record<string, unknown>) => {
    try {
      if (modal?.kind === 'edit') {
        await crud.update(modal.risk.risk_id, values);
        notify({ type: 'success', message: `Risk "${values.risk_id}" updated.` });
      } else {
        await crud.add(values);
        notify({ type: 'success', message: `Risk "${values.risk_id}" added.` });
      }
      setModal(null);
      structured.refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to save risk';
      throw new Error(typeof msg === 'string' ? msg : 'Failed to save risk');
    }
  };

  const handleDelete = async (target: RiskRecord) => {
    const ok = await confirm({
      title: 'Delete risk?',
      description: `Delete risk "${target.risk_id}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      media: <Icon name="delete" />,
    });
    if (!ok) return;
    try {
      await crud.remove(target.risk_id);
      notify({ type: 'success', message: `Risk "${target.risk_id}" deleted.` });
      structured.refetch();
    } catch (e: any) {
      const msg = e?.message || 'Failed to delete risk';
      notify({ type: 'error', title: 'Delete failed', message: msg });
    }
  };

  const handleExport = () => {
    exportRisksToCsv(
      hierarchical.enabled
        ? risks.filter((r: any) => r?.source !== 'child_tara')
        : risks,
    );
  };

  const handleImport = async (file: File) => {
    let records: RiskRecord[];
    try {
      records = await parseRiskFile(file);
    } catch (e: any) {
      notify({
        type: 'error',
        title: 'Import failed',
        message: e?.message || `Could not parse ${file.name}`,
      });
      return;
    }
    if (records.length === 0) {
      notify({ type: 'warning', message: `${file.name} contained no risk rows.` });
      return;
    }

    const existingIds = new Set(risks.map((r) => r.risk_id));
    let added = 0;
    let skipped = 0;
    let failed = 0;
    for (const record of records) {
      if (!record?.risk_id) {
        failed += 1;
        continue;
      }
      if (existingIds.has(record.risk_id)) {
        skipped += 1;
        continue;
      }
      try {
        await crud.add(record as Record<string, unknown>);
        existingIds.add(record.risk_id);
        added += 1;
      } catch {
        failed += 1;
      }
    }

    if (added > 0) structured.refetch();
    const parts: string[] = [];
    if (added) parts.push(`${added} added`);
    if (skipped) parts.push(`${skipped} skipped (duplicate id)`);
    if (failed) parts.push(`${failed} failed`);
    notify({
      type: failed && !added ? 'error' : 'success',
      title: failed && !added ? 'Import failed' : 'Import complete',
      message: parts.join(' · ') || 'No rows processed.',
    });
  };

  const newRisks = hierarchical.enabled
    ? risks.filter((r: any) => r?.source !== 'child_tara')
    : risks;
  const childRisks: ChildRiskView[] = hierarchical.enabled
    ? risks.filter((r: any) => r?.source === 'child_tara')
    : [];

  // Apply the matrix cell selection on top of the analysis-mode
  // split. Hierarchical mode: matrix operates over `newRisks`
  // (current TARA bucket; child TARAs have their own matrix at the
  // source). Individual mode: matrix operates over the full list.
  const matrixSource = hierarchical.enabled ? newRisks : risks;
  const applyMatrixFilter = (list: RiskRecord[]) => {
    if (!matrixFilter) return list;
    return list.filter((r: any) => {
      const pri = r?.treatment_priority ?? 'Low';
      const val = parseInt(String(r?.risk_value ?? ''), 10);
      return pri === matrixFilter.priority && val === matrixFilter.value;
    });
  };
  const visibleNewRisks = applyMatrixFilter(newRisks);
  const visibleRisks = applyMatrixFilter(risks);

  return (
    <div className={styles.bleed}>
      {hierarchical.enabled ? (
        <HierarchicalRiskReview
          newRisks={visibleNewRisks}
          childRisks={childRisks}
          editable={editable}
          onAdd={() => setModal({ kind: 'add' })}
          onEdit={(risk) => setModal({ kind: 'edit', risk })}
          onDelete={(risk) => handleDelete(risk)}
          onExport={handleExport}
          onImport={handleImport}
          modifications={hierarchical.modifications}
          onToggleRelevance={hierarchical.setRelevance}
          onJustificationChange={hierarchical.setJustification}
          childInputsDisabled={hierarchical.approving || !editable}
          onSaveChildModifications={hierarchical.onApprove}
        />
      ) : (
        <RiskTable
          risks={visibleRisks}
          editable={editable}
          onAdd={() => setModal({ kind: 'add' })}
          onEdit={(risk) => setModal({ kind: 'edit', risk })}
          onDelete={(risk) => handleDelete(risk)}
          onExport={handleExport}
          onImport={handleImport}
        />
      )}
      {/* Priority × Value heat map — Story 15.B. Always uses the
          unfiltered `matrixSource` for cell counts so the user can
          see all buckets even while a filter is active. */}
      {matrixSource.length > 0 && (
        <RiskMatrix
          risks={matrixSource}
          filter={matrixFilter}
          onCellSelect={setMatrixFilter}
        />
      )}
      {modal && (
        <RiskFormModal
          mode={modal.kind}
          initialRisk={modal.kind === 'edit' ? modal.risk : null}
          assetOptions={assetOptions}
          threatOptions={threatOptions}
          scenarioOptions={scenarioOptions}
          attackPathOptions={attackPathOptions}
          onSubmit={handleSave}
          onClose={() => setModal(null)}
        />
      )}
      {confirmDialog}
    </div>
  );
}
